import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useBaseIngredientSearchData from 'components/desktop/BaseIngredient/Search/useBaseIngredientSearchData';
import CharacteristicToggleSelectNew from 'components/desktop/_general/CharacteristicSelect/CharacteristicToggleSelectNew';
import Co2Select from 'components/desktop/_general/Co2Select/Co2SelectNew';
import { RenderIf } from 'components/desktop/_general/Conditional/RenderIf';
import MultiSearchSelectNew from 'components/desktop/_general/MultiSearchSelect/MultiSearchSelectNew';
import NutriScoreDiagramSelectNew from 'components/desktop/_general/NutriScoreSelect/NutrieScoreDiagramSelectNew';
import RangeDouble from 'components/desktop/_general/RangeDouble/RangeDoubleNew2';
import ENTITLEMENTS from 'enums/entitlements';
import * as AllergenList from 'types/BaseIngredient/Search/AllergenList';
import * as BaseIngredientSearchResponse from 'types/BaseIngredient/Search/BaseIngredientSearchResponseDto';
import * as RangeWithIdList from 'types/BaseIngredient/Search/RangeWithIdList';
import * as Search from 'types/BaseIngredient/Search/Search';
import * as SliderWithIdList from 'types/BaseIngredient/Search/SliderWithIdList';
import * as NutriScoreLabelList from 'types/NutriScore/NutriScoreLabelList';
import * as CharacteristicToggleList from 'types/_general/Search/CharacteristicToggleList';
import * as Range from 'types/_general/Search/Range';
import * as Slider from 'types/_general/Search/Slider';

interface IProps {
	archive: boolean;
	disabled: boolean;
	handleChangeAllergens: (input: string[]) => void;
	handleChangeCategories: (items: string[]) => void;
	handleChangeCharacteristics: (input: { saved: string; all: string[] }) => void;
	handleChangeCo2Labels: (id: string) => void;
	handleChangeNutriScoreLabels: (input: { saved: string; all: NutriScoreLabelList.Type }) => void;
	handleChangeSliders: (input: { id: string; left: number | null; right: number | null }) => void;
	handleChangeSeasons: (items: string[]) => void;
	handleChangeTags: (items: string[]) => void;
	handleReset: () => void;
	search: Search.Type;
	searchResponse: BaseIngredientSearchResponse.Type;
	showAdvancedSearch: boolean;
}

const AdvancedFilters = (props: IProps) => {
	const { t } = useTranslation();
	const initialCharacteristics = useMemo(() => CharacteristicToggleList.initialize(), []);
	const data = useBaseIngredientSearchData();

	const projection = useMemo(
		() =>
			RangeWithIdList.mergeRangesWithSliders(
				props.archive ? data.rangesArchived : data.ranges,
				props.search
			),
		[data.rangesArchived, data.ranges, props.search]
	);

	const renderCol1 = (): JSX.Element => {
		return (
			<>
				<MultiSearchSelectNew
					allItems={data.tags}
					className="mb-2"
					displaySavedItems="top"
					errorKey="Tags"
					label={t('_general:TAGS')}
					savedItems={props.search.tagIds}
					setSavedItems={props.handleChangeTags}
				/>
				<MultiSearchSelectNew
					allItems={AllergenList.mapToSelectItemList(data.allergens)}
					className="mb-2"
					displaySavedItems="top"
					errorKey="Allergens"
					label={t('_general:ALLERGENS')}
					savedItems={props.search.allergenIds}
					setSavedItems={props.handleChangeAllergens}
				/>
				<RenderIf entitlements={[ENTITLEMENTS.SEASON]}>
					<MultiSearchSelectNew
						allItems={data.seasons}
						className="mb-2"
						displaySavedItems="top"
						errorKey="Seasons"
						label={t('_general:SEASONS')}
						savedItems={props.search.seasonIds}
						setSavedItems={props.handleChangeSeasons}
					/>
				</RenderIf>
				<MultiSearchSelectNew
					allItems={data.baseIngredientCategories}
					className="mb-2"
					displaySavedItems="top"
					errorKey="Categories"
					label={t('_general:CATEGORIES')}
					savedItems={props.search.categoryIds}
					setSavedItems={props.handleChangeCategories}
				/>
			</>
		);
	};

	const renderCol2 = (): JSX.Element[] => {
		return projection
			.filter((e) => e.maximum !== null && e.minimum !== null)
			.map((x, i) => {
				return (
					<div key={i} className="row mb-2 mt-2">
						<div className="col-3">
							<label>{x.label}</label>
						</div>
						<div className="col-9">
							<RangeDoubleLabelWrapper
								handleChange={(left, right) =>
									props.handleChangeSliders({
										id: x.id,
										left: left ?? x.left,
										right: right ?? x.right,
									})
								}
								label={x.label}
								rangeArea={RangeWithIdList.getById(
									props.searchResponse.ranges,
									x.id
								)}
								rangeMinMax={x}
								slider={SliderWithIdList.getById(props.search.sliders, x.id)}
								showAdvancedSearch={props.showAdvancedSearch}
							/>
						</div>
					</div>
				);
			});
	};

	const renderCol3 = (): JSX.Element => {
		return (
			<div className="d-flex flex-column" style={{ gap: '10px' }}>
				<RenderIf entitlements={[ENTITLEMENTS.CARBONDIOXIDE]}>
					<Co2Select
						carbonDioxideLabels={data.co2Labels}
						activeIds={props.search.co2LabelIds}
						setActiveId={props.handleChangeCo2Labels}
					/>
				</RenderIf>
				<RenderIf entitlements={[ENTITLEMENTS.NUTRI_SCORE]}>
					<NutriScoreDiagramSelectNew
						all={data.nutriScoreLabels}
						handleChange={props.handleChangeNutriScoreLabels}
						saved={props.search.nutriScoreLabelIds}
					/>
				</RenderIf>
				<CharacteristicToggleSelectNew
					all={initialCharacteristics}
					handleChange={props.handleChangeCharacteristics}
					saved={props.search.characteristics}
				/>
			</div>
		);
	};

	return (
		<>
			<div className="row">
				<div className="col-lg-4">{renderCol1()}</div>
				<div className="col-lg-6">{renderCol2()}</div>
				<div className="col-lg-2">{renderCol3()}</div>
			</div>
			<div className="row">
				<div className="col-lg-12" style={{ textAlign: 'end' }}>
					<button className="btn btn-primary" onClick={props.handleReset}>
						{t('_general:RESET')}
					</button>
				</div>
			</div>
		</>
	);
};

export default AdvancedFilters;

const RangeDoubleLabelWrapper = (props: {
	rangeArea: Range.Type;
	rangeMinMax: Range.Type;
	slider: Slider.Type;
	label: string;
	handleChange: (left: number | null, right: number | null) => void;
	showAdvancedSearch: boolean;
}) => {
	const { rangeArea, rangeMinMax, slider } = props;
	return (
		<RangeDouble
			areaMax={rangeArea.maximum ?? rangeMinMax.maximum ?? 0}
			areaMin={rangeArea.minimum ?? rangeMinMax.minimum ?? 0}
			defaultControlLeft={slider.left ?? rangeMinMax.minimum ?? 0}
			defaultControlRight={slider.right ?? rangeMinMax.maximum ?? 0}
			handleChange={props.handleChange}
			totalMax={Slider.getMaxRoundUp(rangeMinMax.maximum ?? 0)}
			totalMin={Slider.getMinRoundDown(rangeMinMax.minimum ?? 0)}
			showAdvancedSearch={props.showAdvancedSearch}
		/>
	);
};
